import React, { useState, useEffect } from 'react';
import { BROKERS } from './config/brokers';
import Queue from './pages/Queue';
import ContactCard from './pages/ContactCard';
import LogActivity from './pages/LogActivity';
import Scorecard from './pages/Scorecard';
import Login from './pages/Login';
import EmailCompose from './pages/EmailCompose';
import OppsQueue from './pages/OppsQueue';
import OppsCard from './pages/OppsCard';
import OppsLogActivity from './pages/OppsLogActivity';
import Home from './pages/Home';
import { setGmailTokens } from './utils/gmail';
import { getAuthSession, setAuthSession, clearAuthSession } from './utils/auth';

// ── Bottom tab nav ─────────────────────────────────────────────────────────
const TAB_NAV_HEIGHT = 60;

function BottomNav({ activeTab, onSwitch }) {
  const tabs = [
    { id: 'home',    label: 'Home',     icon: '🏠' },
    { id: 'queue',   label: 'Prospect', icon: '📋' },
    { id: 'opps',    label: 'Opps',     icon: '🏢' },
  ];
  return (
    <div style={{
      position: 'fixed', bottom: 0, left: 0, right: 0,
      height: `${TAB_NAV_HEIGHT}px`,
      backgroundColor: '#fff',
      borderTop: '1px solid #e8e6e0',
      display: 'flex',
      zIndex: 100,
    }}>
      {tabs.map((tab) => {
        const active = activeTab === tab.id;
        return (
          <button
            key={tab.id}
            onClick={() => onSwitch(tab.id)}
            style={{
              flex: 1, border: 'none', background: 'none',
              cursor: 'pointer',
              display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center',
              gap: '2px', padding: 0, position: 'relative',
              color: active ? '#534AB7' : '#9ca3af',
            }}
          >
            <span style={{ fontSize: '20px', lineHeight: 1 }}>{tab.icon}</span>
            <span style={{ fontSize: '10px', fontWeight: active ? '700' : '500', letterSpacing: '0.04em' }}>
              {tab.label}
            </span>
            {active && (
              <div style={{
                position: 'absolute', bottom: 0,
                width: '40px', height: '3px',
                backgroundColor: '#534AB7', borderRadius: '2px 2px 0 0',
              }} />
            )}
          </button>
        );
      })}
    </div>
  );
}

// ── App ────────────────────────────────────────────────────────────────────
export default function App() {
  const [session, setSession]           = useState(() => getAuthSession());
  const [authError, setAuthError]       = useState(null);
  const [tab, setTab]                   = useState('home');   // 'home' | 'queue' | 'opps'
  const [view, setView]                 = useState('list');   // 'list' | 'contact' | 'log' | 'email' | 'opps-card' | 'opps-log' | 'scorecard'
  const [selectedItem, setSelectedItem] = useState(null);    // prospecting item
  const [selectedOpp, setSelectedOpp]   = useState(null);    // opps item
  const [skippedIds, setSkippedIds]     = useState(new Set());
  const [emailContact, setEmailContact] = useState(null);
  const [emailSource, setEmailSource]   = useState('queue'); // 'queue' | 'opps'
  const [viewAs, setViewAs]             = useState(null);    // admin override: view as another broker

  // Admins can switch to any broker's view without logging out
  // isAdmin: prefer session flag, but fall back to BROKERS list in case the session
  // was saved before isAdmin was added to the auth flow (avoids requiring re-login).
  const isAdmin = session?.isAdmin || BROKERS.find(b => b.name === session?.brokerName)?.isAdmin || false;
  const broker  = (isAdmin && viewAs) ? viewAs : (session?.brokerName || null);

  // ── Handle OAuth callbacks ────────────────────────────────────────────────
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    const authBroker   = params.get('auth_broker');
    const authEmail    = params.get('auth_email');
    const authName     = params.get('auth_name');
    const authIsAdmin  = params.get('auth_is_admin');
    const authPicture  = params.get('auth_picture');
    const authErrorVal = params.get('auth_error');

    if (authBroker) {
      const newSession = {
        brokerName: authBroker,
        email:      authEmail   || '',
        name:       authName    || authBroker,
        isAdmin:    authIsAdmin === 'true',
        picture:    authPicture || '',
      };
      setAuthSession(newSession);
      setSession(newSession);
      setAuthError(null);
      window.history.replaceState({}, '', '/');
      return;
    }

    if (authErrorVal) {
      setAuthError(authErrorVal);
      window.history.replaceState({}, '', '/');
      return;
    }

    const accessToken      = params.get('gmail_access_token');
    const refreshToken     = params.get('gmail_refresh_token');
    const expiry           = params.get('gmail_expiry');
    const gmailBroker      = params.get('gmail_broker');
    const gmailError       = params.get('gmail_error');
    const gmailEmail       = params.get('gmail_email');
    const gmailDisplayName = params.get('gmail_display_name');
    const gmailSig         = params.get('gmail_signature');

    if (accessToken) {
      const targetBroker = gmailBroker || broker;
      if (targetBroker) {
        setGmailTokens(targetBroker, {
          accessToken,
          refreshToken,
          expiry:      Number(expiry),
          email:       gmailEmail       || '',
          displayName: gmailDisplayName || '',
          signature:   gmailSig         || '',
        });
      }
      window.history.replaceState({}, '', '/');
    }

    if (gmailError) {
      console.warn('Gmail auth error:', gmailError);
      window.history.replaceState({}, '', '/');
    }
  }, []); // eslint-disable-line

  function handleSignOut() {
    clearAuthSession();
    setSession(null);
    setSelectedItem(null);
    setSelectedOpp(null);
    setSkippedIds(new Set());
    setViewAs(null);
    setTab('home');
    setView('list');
  }

  if (!session) {
    return <Login authError={authError} />;
  }

  // ── Tab switching ──────────────────────────────────────────────────────────
  function switchTab(newTab) {
    setTab(newTab);
    setView('list');
    setSelectedItem(null);
    setSelectedOpp(null);
    setEmailContact(null);
  }

  // ── Queue navigation ───────────────────────────────────────────────────────
  function openContact(item)  { setSelectedItem(item); setView('contact'); }
  function openLog()          { setView('log'); }
  function backToContact()    { setView('contact'); }

  function backToQueue() {
    setSelectedItem(null);
    setEmailContact(null);
    setView('list');
  }

  function skipItem(itemId) {
    setSkippedIds((prev) => new Set([...prev, itemId]));
    setSelectedItem(null);
    setView('list');
  }

  function onActivitySaved() {
    setSelectedItem(null);
    setView('list');
  }

  function openEmail(contactData, source = 'queue') {
    setEmailContact(contactData);
    setEmailSource(source);
    setView('email');
  }

  function backFromEmail() {
    setEmailContact(null);
    if (emailSource === 'opps') {
      setView('opps-card');
    } else {
      setView('contact');
    }
  }

  // ── Opps navigation ────────────────────────────────────────────────────────
  function openOpp(item) { setSelectedOpp(item); setView('opps-card'); }

  function backToOpps() {
    setSelectedOpp(null);
    setEmailContact(null);
    setView('list');
  }

  function openOppsLog()    { setView('opps-log'); }
  function backToOppsCard() { setView('opps-card'); }

  function onOppsActivitySaved() {
    setView('opps-card');
  }

  // ── Full-screen views (no tab nav) ──────────────────────────────────────
  if (view === 'email' && emailContact) {
    return (
      <EmailCompose
        contact={emailContact}
        brokerName={broker}
        onSent={backFromEmail}
        onCancel={backFromEmail}
      />
    );
  }

  if (view === 'scorecard') {
    return <Scorecard onBack={() => setView('list')} />;
  }

  if (tab === 'queue' && view === 'log' && selectedItem) {
    return (
      <LogActivity
        prospectItem={selectedItem}
        brokerName={broker}
        onSaved={onActivitySaved}
        onCancel={backToContact}
      />
    );
  }

  if (tab === 'opps' && view === 'opps-log' && selectedOpp) {
    return (
      <OppsLogActivity
        oppsItem={selectedOpp}
        brokerName={broker}
        onSaved={onOppsActivitySaved}
        onCancel={backToOppsCard}
      />
    );
  }

  // ── All other views include the bottom nav ─────────────────────────────────
  return (
    <div style={{ paddingBottom: `${TAB_NAV_HEIGHT}px` }}>

      {/* Home */}
      {tab === 'home' && view === 'list' && (
        <Home
          brokerName={broker}
          isAdmin={isAdmin}
          viewAs={viewAs}
          onViewAs={(name) => { setViewAs(name); switchTab('home'); }}
          onGoToProspect={() => switchTab('queue')}
          onGoToOpps={() => switchTab('opps')}
        />
      )}

      {/* Prospect list */}
      {tab === 'queue' && view === 'list' && (
        <Queue
          brokerName={broker}
          onSelectItem={openContact}
          skippedIds={skippedIds}
          onOpenScorecard={() => setView('scorecard')}
          onSwitchBroker={handleSignOut}
        />
      )}

      {/* Prospect contact card */}
      {tab === 'queue' && view === 'contact' && selectedItem && (
        <ContactCard
          prospectItem={selectedItem}
          brokerName={broker}
          onBack={backToQueue}
          onLogActivity={openLog}
          onSkip={skipItem}
          onEmail={(contactData) => openEmail(contactData, 'queue')}
        />
      )}

      {/* Opps list */}
      {tab === 'opps' && view === 'list' && (
        <OppsQueue
          brokerName={broker}
          onSelectItem={openOpp}
        />
      )}

      {/* Opps card */}
      {tab === 'opps' && view === 'opps-card' && selectedOpp && (
        <OppsCard
          oppsItem={selectedOpp}
          brokerName={broker}
          onBack={backToOpps}
          onLogActivity={openOppsLog}
          onEmail={(contactData) => openEmail(contactData, 'opps')}
        />
      )}

      <BottomNav activeTab={tab} onSwitch={switchTab} />
    </div>
  );
}
